import React, { useState, useEffect } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";

/* ─── Add Business Modal ──────────────────────────────────────────────
   Shared popup used by: Admin → Businesses → "New Business", and
   Admin → Direct Orders → Customer → "Create new business".
   onCreated(business) receives the freshly created business record.
   ──────────────────────────────────────────────────────────────────── */
const AddBusinessModal = ({ onClose, onCreated }) => {
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    userId: "",
    companyName: "",
    companyType: "sole_proprietorship",
    industry: "",
    website: "",
    gstNumber: "",
    panNumber: "",
    contactPersonName: "",
    contactPersonDesignation: "",
    contactPersonPhone: "",
    contactPersonEmail: "",
    regAddressLine1: "",
    regAddressLine2: "",
    regCity: "",
    regState: "",
    regPincode: "",
    regCountry: "India",
    commAddressLine1: "",
    commAddressLine2: "",
    commCity: "",
    commState: "",
    commPincode: "",
    commCountry: "India",
    bankName: "",
    accountNumber: "",
    branchName: "",
    ifscCode: "",
    isVerified: true,
  });

  useEffect(() => {
    API.get("/admin/users?role=user")
      .then((r) => setUserOptions(r.data.users || []))
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, []);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.companyName.trim() ||
      !form.contactPersonName.trim() ||
      !form.contactPersonPhone.trim() ||
      !form.contactPersonEmail.trim() ||
      !form.regAddressLine1.trim() ||
      !form.regCity.trim() ||
      !form.regState.trim() ||
      !form.regPincode.trim()
    ) {
      toast.error("Please fill all required fields.");
      return;
    }
    try {
      setSaving(true);
      const { data } = await API.post("/business/admin", form);
      toast.success("Business created!");
      onCreated(data.business);
    } catch (err) {
      const errData = err.response?.data;
if (errData?.errors?.length) {
  errData.errors.forEach(e => toast.error(e));
} else {
  toast.error(errData?.message || 'Failed to create business.');
}
    } finally {
      setSaving(false);
    }
  };

  const field = (label, name, props = {}) => (
    <div>
      <label
        style={{
          fontSize: "0.82rem",
          fontWeight: 600,
          display: "block",
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      <input
        className="form-control"
        value={form[name]}
        onChange={(e) => {
  set(name, e.target.value);
  if (name === "gstNumber") {
    const g = e.target.value.trim().toUpperCase();
    set("panNumber", g.length >= 12 ? g.slice(2, 12) : "");
  }
}}
        {...props}
      />
    </div>
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={undefined}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          background: "#fff",
          borderRadius: "var(--radius-lg)",
          padding: 32,
          maxWidth: 640,
          width: "100%",
          maxHeight: "88vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h3>➕ Add New Business</h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              fontSize: "1.2rem",
              cursor: "pointer",
              color: "var(--text-muted)",
            }}
          >
            ✕
          </button>
        </div>

        <h4 style={{ fontSize: "0.9rem", marginBottom: 10 }}>
          Company Details
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 16,
          }}
        >
          {field("Company Name *", "companyName", { required: true })}
          <div>
            <label
              style={{
                fontSize: "0.82rem",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Company Type *
            </label>
            <select
              className="form-control"
              value={form.companyType}
              onChange={(e) => set("companyType", e.target.value)}
            >
              <option value="sole_proprietorship">Sole Proprietorship</option>
              <option value="partnership">Partnership</option>
              <option value="private_limited">Private Limited</option>
              <option value="public_limited">Public Limited</option>
              <option value="llp">LLP</option>
              <option value="ngo">NGO / Trust</option>
              <option value="other">Other</option>
            </select>
          </div>
          {field("Industry", "industry")}
          {field("Website", "website")}
          {field("GST Number", "gstNumber")}
          {field("PAN Number", "panNumber")}
        </div>

        <h4 style={{ fontSize: "0.9rem", marginBottom: 10 }}>Contact Person</h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 16,
          }}
        >
          {field("Contact Name *", "contactPersonName", { required: true })}
          {field("Designation", "contactPersonDesignation")}
          {field("Contact Phone *", "contactPersonPhone", { required: true })}
          {field("Contact Email *", "contactPersonEmail", {
            type: "email",
            required: true,
          })}
        </div>

        <h4 style={{ fontSize: "0.9rem", marginBottom: 10 }}>Address</h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div style={{ gridColumn: "1 / -1" }}>
            {field("Address Line 1 *", "regAddressLine1", { required: true })}
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            {field("Address Line 2", "regAddressLine2")}
          </div>
          {field("City *", "regCity", { required: true })}
          {field("State *", "regState", { required: true })}
          {field("Pincode *", "regPincode", { required: true })}
          {field("Country", "regCountry")}
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: "0.85rem",
            marginBottom: 20,
          }}
        >
          <input
            type="checkbox"
            checked={form.isVerified}
            onChange={(e) => set("isVerified", e.target.checked)}
          />
          Mark as approved immediately
        </label>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary"
            style={{ flex: 1, justifyContent: "center" }}
          >
            {saving ? "Creating…" : "Create Business"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline"
            style={{ flex: 1, justifyContent: "center" }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBusinessModal;
